// src/utils/occasions.js
// Dynamic occasion data that pulls from template categories
// Rich SEO content for known occasions, auto-generated for unknown ones

/**
 * Known occasions with rich SEO content
 * Keys should match your template category names (case-insensitive matching)
 */
export const KNOWN_OCCASIONS = {
  christmas: {
    emoji: '🎄',
    heroTitle: 'Christmas Greeting Cards',
    heroSubtitle: 'Create magical Christmas cards that capture the joy and warmth of the holiday season',
    description: 'Design beautiful Christmas greeting cards with AI. Choose from festive templates featuring Santa, snowflakes, Christmas trees, and winter wonderlands. Perfect for family, friends, and colleagues.',
    metaDescription: 'Create stunning Christmas greeting cards with AI. Professional templates, easy customization, instant digital delivery. Make this holiday season special with personalized Christmas cards.',
    keywords: ['Christmas cards', 'holiday cards', 'Merry Christmas cards', 'Christmas greeting cards', 'digital Christmas cards'],
    features: [
      { icon: '🎅', title: 'Festive Templates', description: 'Dozens of Christmas-themed designs from elegant to fun' },
      { icon: '✨', title: 'AI Magic', description: 'Generate unique Christmas scenes with AI technology' },
      { icon: '📧', title: 'Instant Delivery', description: 'Send beautiful cards via email or shareable link' },
      { icon: '👨‍👩‍👧‍👦', title: 'Group Cards', description: 'Collaborate with family on a single card' },
    ],
    messageIdeas: [
      'Wishing you a Merry Christmas and a Happy New Year!',
      'May your holidays be filled with warmth and joy.',
      'Sending you love and holiday cheer!',
      'Here\'s to a season of peace, love, and happiness.',
    ],
    gradient: 'from-rose-700 to-rose-900',
    accentColor: 'rose',
  },
  hanukkah: {
    emoji: '🕎',
    heroTitle: 'Hanukkah Greeting Cards',
    heroSubtitle: 'Celebrate the Festival of Lights with beautiful, personalized Hanukkah cards',
    description: 'Create stunning Hanukkah greeting cards featuring menorahs, dreidels, Star of David, and traditional blue and gold designs. Perfect for sharing the joy of the eight nights.',
    metaDescription: 'Design beautiful Hanukkah greeting cards with AI. Professional templates featuring menorahs, dreidels, and traditional designs. Celebrate the Festival of Lights.',
    keywords: ['Hanukkah cards', 'Chanukah cards', 'Festival of Lights cards', 'Jewish holiday cards'],
    features: [
      { icon: '🕯️', title: 'Traditional Designs', description: 'Menorahs, dreidels, and Star of David motifs' },
      { icon: '💙', title: 'Elegant Themes', description: 'Beautiful blue and gold color schemes' },
      { icon: '✡️', title: 'Meaningful Messages', description: 'Templates for all eight nights' },
      { icon: '🎁', title: 'Gift Tags', description: 'Matching Hanukkah gift tags available' },
    ],
    messageIdeas: [
      'Happy Hanukkah! May your home be filled with light and love.',
      'Wishing you eight nights of joy and celebration.',
      'May the Festival of Lights bring peace to your heart.',
      'Chag Sameach! Happy Hanukkah to you and your family.',
    ],
    gradient: 'from-blue-700 to-blue-900',
    accentColor: 'blue',
  },
  'new year': {
    emoji: '🎆',
    heroTitle: 'New Year Greeting Cards',
    heroSubtitle: 'Ring in the new year with stunning cards that celebrate fresh beginnings',
    description: 'Design spectacular New Year greeting cards featuring fireworks, champagne, countdowns, and celebratory designs.',
    metaDescription: 'Create stunning New Year greeting cards with AI. Professional templates with fireworks and celebration designs. Send personalized Happy New Year wishes instantly.',
    keywords: ['New Year cards', 'Happy New Year cards', 'New Year greeting cards', 'New Year 2025 cards'],
    features: [
      { icon: '🎇', title: 'Celebration Themes', description: 'Fireworks, confetti, and party designs' },
      { icon: '🥂', title: 'Elegant Options', description: 'Sophisticated champagne and gold themes' },
      { icon: '📅', title: 'Year Customization', description: 'Add the new year number prominently' },
      { icon: '🌟', title: 'Fresh Start Vibes', description: 'Inspiring designs for new beginnings' },
    ],
    messageIdeas: [
      'Happy New Year! May 2025 bring you joy and success.',
      'Cheers to a new year filled with new possibilities!',
      'Wishing you health, happiness, and prosperity in the new year.',
      'Out with the old, in with the new! Happy New Year!',
    ],
    gradient: 'from-slate-800 to-slate-900',
    accentColor: 'slate',
  },
  birthday: {
    emoji: '🎂',
    heroTitle: 'Birthday Greeting Cards',
    heroSubtitle: 'Make their special day unforgettable with personalized birthday cards',
    description: 'Create joyful birthday greeting cards for all ages. From playful designs for kids to elegant options for adults.',
    metaDescription: 'Design personalized birthday greeting cards with AI. Templates for all ages, easy customization, instant delivery.',
    keywords: ['birthday cards', 'Happy Birthday cards', 'birthday greeting cards', 'personalized birthday cards'],
    features: [
      { icon: '🎈', title: 'All Ages', description: 'Designs for kids, teens, adults, and seniors' },
      { icon: '🎁', title: 'Party Themes', description: 'Balloons, cake, and celebration motifs' },
      { icon: '📸', title: 'Photo Cards', description: 'Add their photo for a personal touch' },
      { icon: '🎵', title: 'Fun Elements', description: 'Playful and vibrant design options' },
    ],
    messageIdeas: [
      'Happy Birthday! Wishing you a day as special as you are.',
      'Another year older, another year wiser! Happy Birthday!',
      'May all your birthday wishes come true!',
      'Cheers to you on your special day! Happy Birthday!',
    ],
    gradient: 'from-violet-600 to-violet-800',
    accentColor: 'violet',
  },
  valentine: {
    emoji: '💕',
    heroTitle: "Valentine's Day Cards",
    heroSubtitle: 'Express your love with beautiful, heartfelt Valentine cards',
    description: "Create romantic Valentine's Day greeting cards for your special someone.",
    metaDescription: "Design beautiful Valentine's Day cards with AI. Romantic templates, personalized messages, instant delivery.",
    keywords: ["Valentine's Day cards", "Valentine cards", "love cards", "romantic cards"],
    features: [
      { icon: '❤️', title: 'Romantic Themes', description: 'Hearts, roses, and love-inspired designs' },
      { icon: '💌', title: 'Love Letters', description: 'Beautiful templates for heartfelt messages' },
      { icon: '🌹', title: 'Elegant Options', description: 'Sophisticated designs for adults' },
      { icon: '💝', title: 'Cute & Fun', description: 'Playful designs for friends and family' },
    ],
    messageIdeas: [
      "You're the love of my life. Happy Valentine's Day!",
      "Every day with you is Valentine's Day.",
      'Sending you all my love on this special day.',
      "You make my heart smile. Happy Valentine's Day!",
    ],
    gradient: 'from-rose-600 to-rose-800',
    accentColor: 'rose',
  },
  "mother's day": {
    emoji: '💐',
    heroTitle: "Mother's Day Cards",
    heroSubtitle: "Show Mom how much she means to you with a heartfelt card",
    description: "Create beautiful Mother's Day greeting cards to celebrate the amazing women in your life.",
    metaDescription: "Design beautiful Mother's Day cards with AI. Elegant floral templates, personalized messages.",
    keywords: ["Mother's Day cards", "Mom cards", "cards for Mom"],
    features: [
      { icon: '🌸', title: 'Floral Designs', description: 'Beautiful flower arrangements and botanical themes' },
      { icon: '💝', title: 'Heartfelt Templates', description: 'Designs that express gratitude and love' },
      { icon: '👩‍👧', title: 'Family Themes', description: 'Celebrate the mother-child bond' },
      { icon: '✨', title: 'Elegant Options', description: 'Sophisticated designs for any mom' },
    ],
    messageIdeas: [
      "Happy Mother's Day to the best mom in the world!",
      'Thank you for everything you do. We love you, Mom!',
      "Mom, you're my hero. Happy Mother's Day!",
      "To the woman who does it all – Happy Mother's Day!",
    ],
    gradient: 'from-pink-600 to-pink-800',
    accentColor: 'pink',
  },
  "father's day": {
    emoji: '👔',
    heroTitle: "Father's Day Cards",
    heroSubtitle: 'Celebrate Dad with a card that shows how much he means to you',
    description: "Create meaningful Father's Day greeting cards for the amazing dad in your life.",
    metaDescription: "Design personalized Father's Day cards with AI. Classic and fun templates.",
    keywords: ["Father's Day cards", "Dad cards", "cards for Dad"],
    features: [
      { icon: '🏆', title: 'Best Dad Themes', description: 'Celebrate Dad with trophy and award designs' },
      { icon: '🎣', title: 'Hobby Inspired', description: 'Golf, fishing, grilling, and more' },
      { icon: '💪', title: 'Superhero Dad', description: 'Fun designs for the hero in your life' },
      { icon: '🤵', title: 'Classic Elegance', description: 'Distinguished and sophisticated options' },
    ],
    messageIdeas: [
      "Happy Father's Day to the world's greatest dad!",
      "Dad, you're my hero. Thank you for everything.",
      "To the man who taught me everything – Happy Father's Day!",
      "World's Best Dad – that's you! Happy Father's Day!",
    ],
    gradient: 'from-slate-700 to-slate-900',
    accentColor: 'slate',
  },
  graduation: {
    emoji: '🎓',
    heroTitle: 'Graduation Cards',
    heroSubtitle: 'Celebrate their achievement with a card that marks this milestone',
    description: 'Create celebratory graduation cards for high school, college, or any educational achievement.',
    metaDescription: 'Design graduation cards with AI. Celebratory templates for high school, college, and beyond.',
    keywords: ['graduation cards', 'congratulations graduate cards', 'college graduation cards'],
    features: [
      { icon: '🎓', title: 'Cap & Gown', description: 'Classic graduation imagery and themes' },
      { icon: '🌟', title: 'Achievement Focus', description: 'Celebrate their accomplishments' },
      { icon: '🚀', title: 'Future Forward', description: 'Inspiring designs for their journey ahead' },
      { icon: '📸', title: 'Photo Ready', description: 'Add their graduation photo' },
    ],
    messageIdeas: [
      'Congratulations, Graduate! The world is yours!',
      'You did it! So proud of all your hard work.',
      'The tassel was worth the hassle! Congrats!',
      'Your future is bright. Congratulations on graduating!',
    ],
    gradient: 'from-indigo-700 to-indigo-900',
    accentColor: 'indigo',
  },
  'thank you': {
    emoji: '🙏',
    heroTitle: 'Thank You Cards',
    heroSubtitle: 'Express your gratitude with a beautifully designed thank you card',
    description: 'Create heartfelt thank you cards for any occasion.',
    metaDescription: 'Design thank you cards with AI. Beautiful templates for any occasion.',
    keywords: ['thank you cards', 'gratitude cards', 'appreciation cards'],
    features: [
      { icon: '💐', title: 'Elegant Designs', description: 'Sophisticated floral and botanical themes' },
      { icon: '✍️', title: 'Personal Touch', description: 'Space for heartfelt messages' },
      { icon: '🎁', title: 'Gift Thanks', description: 'Perfect for post-gift gratitude' },
      { icon: '💼', title: 'Professional', description: 'Business thank you options' },
    ],
    messageIdeas: [
      'Thank you so much for your kindness and generosity.',
      'Your thoughtfulness means the world to me.',
      "I'm so grateful for everything you've done.",
      'A heartfelt thank you for being so wonderful.',
    ],
    gradient: 'from-emerald-700 to-emerald-900',
    accentColor: 'emerald',
  },
  wedding: {
    emoji: '💒',
    heroTitle: 'Wedding Cards',
    heroSubtitle: 'Celebrate love and new beginnings with elegant wedding cards',
    description: 'Create beautiful wedding greeting cards for the happy couple.',
    metaDescription: 'Design elegant wedding cards with AI. Beautiful templates for congratulations and more.',
    keywords: ['wedding cards', 'wedding congratulations cards', 'marriage cards'],
    features: [
      { icon: '💍', title: 'Elegant Themes', description: 'Sophisticated designs for the big day' },
      { icon: '🌸', title: 'Floral Beauty', description: 'Romantic flower arrangements' },
      { icon: '✨', title: 'Gold Accents', description: 'Luxurious metallic touches' },
      { icon: '📸', title: 'Photo Cards', description: 'Feature the happy couple' },
    ],
    messageIdeas: [
      'Congratulations on your wedding! Wishing you a lifetime of love.',
      'May your marriage be filled with joy, love, and laughter.',
      "Here's to love, laughter, and happily ever after!",
      'Two hearts, one love. Congratulations!',
    ],
    gradient: 'from-stone-600 to-stone-800',
    accentColor: 'stone',
  },
  baby: {
    emoji: '👶',
    heroTitle: 'Baby Shower Cards',
    heroSubtitle: 'Welcome the little one with adorable baby shower cards',
    description: 'Create sweet and adorable baby shower cards to celebrate the upcoming arrival.',
    metaDescription: 'Design adorable baby shower cards with AI. Cute templates for boys, girls, or gender-neutral.',
    keywords: ['baby shower cards', 'new baby cards', 'baby congratulations cards'],
    features: [
      { icon: '🍼', title: 'Baby Themes', description: 'Bottles, rattles, and nursery motifs' },
      { icon: '🧸', title: 'Cute Animals', description: 'Adorable teddy bears and woodland creatures' },
      { icon: '💙💗', title: 'Boy or Girl', description: 'Gender-specific or neutral options' },
      { icon: '🌈', title: 'Soft Colors', description: 'Gentle pastels and warm tones' },
    ],
    messageIdeas: [
      'Congratulations on your little bundle of joy!',
      'Welcome to the world, little one!',
      'So excited to meet the newest addition to your family!',
      'Wishing you sleepless nights and endless love!',
    ],
    gradient: 'from-sky-600 to-sky-800',
    accentColor: 'sky',
  },
  'get well': {
    emoji: '🌻',
    heroTitle: 'Get Well Soon Cards',
    heroSubtitle: 'Send warm wishes for a speedy recovery',
    description: "Create comforting get well soon cards to brighten someone's day.",
    metaDescription: 'Design get well soon cards with AI. Comforting templates with warm wishes.',
    keywords: ['get well cards', 'get well soon cards', 'feel better cards'],
    features: [
      { icon: '🌷', title: 'Cheerful Designs', description: 'Bright flowers and uplifting imagery' },
      { icon: '☀️', title: 'Sunny Themes', description: 'Warm and hopeful color palettes' },
      { icon: '💛', title: 'Caring Messages', description: 'Templates with comforting words' },
      { icon: '🤗', title: 'Virtual Hugs', description: 'Designs that offer comfort' },
    ],
    messageIdeas: [
      'Get well soon! Sending healing thoughts your way.',
      'Wishing you a speedy recovery!',
      'Take it easy and feel better soon!',
      'Thinking of you and hoping you feel better every day.',
    ],
    gradient: 'from-amber-600 to-amber-800',
    accentColor: 'amber',
  },
  sympathy: {
    emoji: '🕊️',
    heroTitle: 'Sympathy Cards',
    heroSubtitle: 'Express your condolences with dignity and compassion',
    description: 'Create thoughtful sympathy cards to offer comfort during difficult times.',
    metaDescription: 'Design thoughtful sympathy cards with AI. Elegant, respectful templates.',
    keywords: ['sympathy cards', 'condolence cards', 'bereavement cards'],
    features: [
      { icon: '🌿', title: 'Peaceful Imagery', description: 'Serene nature and botanical designs' },
      { icon: '🕯️', title: 'Respectful Tones', description: 'Soft, muted color palettes' },
      { icon: '💜', title: 'Comforting Words', description: 'Templates with gentle messages' },
      { icon: '🌸', title: 'Elegant Florals', description: 'Classic lilies and roses' },
    ],
    messageIdeas: [
      'With deepest sympathy during this difficult time.',
      'Thinking of you and your family with love.',
      'May cherished memories bring you comfort.',
      'Sending love and strength during this time of loss.',
    ],
    gradient: 'from-slate-600 to-slate-800',
    accentColor: 'slate',
  },
  anniversary: {
    emoji: '💑',
    heroTitle: 'Anniversary Cards',
    heroSubtitle: 'Celebrate years of love with a beautiful anniversary card',
    description: 'Create romantic anniversary cards to celebrate milestones in love.',
    metaDescription: 'Design anniversary cards with AI. Romantic templates for all milestones.',
    keywords: ['anniversary cards', 'wedding anniversary cards', 'happy anniversary cards'],
    features: [
      { icon: '💕', title: 'Romantic Themes', description: 'Hearts, flowers, and love motifs' },
      { icon: '🏆', title: 'Milestone Markers', description: 'Designs for 1st, 25th, 50th, and more' },
      { icon: '📸', title: 'Photo Integration', description: 'Add photos of the happy couple' },
      { icon: '✨', title: 'Elegant Options', description: 'Sophisticated gold and silver themes' },
    ],
    messageIdeas: [
      "Happy Anniversary! Here's to many more years of love.",
      'Celebrating your love story – Happy Anniversary!',
      'Still going strong! Happy Anniversary to you both.',
      'Your love inspires us all. Happy Anniversary!',
    ],
    gradient: 'from-amber-700 to-amber-900',
    accentColor: 'amber',
  },
};

// Default gradients to cycle through for unknown occasions (clean, professional single-tone gradients)
const DEFAULT_GRADIENTS = [
  'from-indigo-700 to-indigo-900',
  'from-slate-700 to-slate-900',
  'from-emerald-700 to-emerald-900',
  'from-violet-700 to-violet-900',
  'from-cyan-700 to-cyan-900',
];

// Default emojis to use for unknown occasions
const DEFAULT_EMOJIS = ['🎴', '💌', '✉️', '📬', '🎁', '🌟', '💫', '🎨'];

/**
 * Convert category name to URL-friendly slug
 */
export function categoryToSlug(category) {
  return category
    .toLowerCase()
    .replace(/[']/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Convert slug back to display name
 */
export function slugToDisplayName(slug) {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Find known occasion data by matching category name
 * Tries exact match, lowercase match, and slug match
 */
export function findKnownOccasion(categoryName) {
  const lowerName = categoryName.toLowerCase();
  const slug = categoryToSlug(categoryName);
  
  // Try exact key match
  if (KNOWN_OCCASIONS[lowerName]) {
    return KNOWN_OCCASIONS[lowerName];
  }
  
  // Try slug match
  if (KNOWN_OCCASIONS[slug]) {
    return KNOWN_OCCASIONS[slug];
  }
  
  // Try partial match (e.g., "Valentine's Day" -> "valentine")
  for (const [key, value] of Object.entries(KNOWN_OCCASIONS)) {
    if (lowerName.includes(key) || key.includes(lowerName)) {
      return value;
    }
  }
  
  return null;
}

/**
 * Generate occasion data for a category
 * Uses known data if available, generates sensible defaults otherwise
 */
export function getOccasionData(categoryName, index = 0) {
  const known = findKnownOccasion(categoryName);
  const slug = categoryToSlug(categoryName);
  const displayName = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
  
  if (known) {
    return {
      ...known,
      name: displayName,
      slug,
      category: categoryName, // Original category name for API calls
    };
  }
  
  // Generate data for unknown occasion
  return {
    name: displayName,
    slug,
    category: categoryName,
    emoji: DEFAULT_EMOJIS[index % DEFAULT_EMOJIS.length],
    heroTitle: `${displayName} Greeting Cards`,
    heroSubtitle: `Create beautiful ${displayName.toLowerCase()} cards with personalized designs`,
    description: `Design stunning ${displayName.toLowerCase()} greeting cards with AI. Choose from professional templates or generate unique designs.`,
    metaDescription: `Create ${displayName.toLowerCase()} greeting cards with AI. Professional templates, easy customization, instant delivery.`,
    keywords: [`${displayName.toLowerCase()} cards`, `${displayName.toLowerCase()} greeting cards`, 'personalized cards'],
    features: [
      { icon: '🎨', title: 'Beautiful Templates', description: `Professional ${displayName.toLowerCase()} designs` },
      { icon: '✨', title: 'AI Generation', description: 'Create unique designs with AI' },
      { icon: '📧', title: 'Instant Delivery', description: 'Send via email or shareable link' },
      { icon: '✏️', title: 'Easy Customization', description: 'Add your own text and photos' },
    ],
    messageIdeas: [
      `Wishing you all the best on this special occasion!`,
      `Sending warm thoughts your way.`,
      `Hope this card brings a smile to your face!`,
      `With love and best wishes.`,
    ],
    gradient: DEFAULT_GRADIENTS[index % DEFAULT_GRADIENTS.length],
    accentColor: 'indigo',
  };
}

/**
 * Build full occasions list from API categories
 */
export function buildOccasionsFromCategories(categories) {
  return categories.map((category, index) => getOccasionData(category, index));
}


